import {
  ClientWorkFlowStatus,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../modules/BulkActions";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import {
  paymentsService,
  PayablesListRequestQuery
} from "../../../services/payments";
import { getAllEntries } from "../../../utils/serviceHelper";
import { PayablesTableItemData } from "../../components/NewPayablesTable/PayablesTable";
import {
  API_QUERY_PAYABLES_DUAL_STAGE_TO_APPROVE,
  API_QUERY_PAYABLES_DUAL_STAGE_TO_PREAPPROVE,
  API_QUERY_PAYABLES_TO_APPROVE
} from "./apiQueries";

async function fetchPayables(apiQuery: PayablesListRequestQuery) {
  const { filter, sort, page, ...other } = apiQuery;
  return getAllEntries(
    async (...args) => {
      return (await paymentsService.payable.list(...(args as any))).data;
    },
    filter,
    sort,
    other
  );
}

async function processApprovePayables(
  data: PayablesTableItemData[],
  workflowStatus: ClientWorkFlowStatus,
  onProgress?: BulkActionOnProgress
): Promise<BulkActionProgress> {
  const bulkAction = bulkActionFactory<PayablesTableItemData>({
    onProgress,
    action: async item => {
      const payableId = item.payable?.payableId || "";

      if (item.payable?.status === InvoiceStatus.Paid) {
        return;
      }

      return await paymentsService.payable.update(payableId, {
        client: { workflowStatus }
      });
    },
    onFinish: ({ successItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });
    }
  });

  const { successItems, failedItems } = await bulkAction.run(data);

  return {
    total: data.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  };
}

export function useApproveAllBulkAction() {
  const runBulkAction = useForegroundBulkAction();

  return (itemCount: number) =>
    runBulkAction({
      actionName: "Approve payables",
      itemCount,
      confirmDescription: `Are you sure you want to approve ${itemCount.toLocaleString()} payable${itemCount > 1 ? "s" : ""}?`,
      confirmLabel: "Yes, approve",
      getItemCount: data => data.length,
      prepare: async () => {
        const fetched = await fetchPayables(API_QUERY_PAYABLES_TO_APPROVE);
        return fetched.map<PayablesTableItemData>(payable => ({ payable }));
      },
      process: async (payables, onProgress) => {
        return processApprovePayables(
          payables,
          ClientWorkFlowStatus.Approved,
          onProgress
        );
      }
    });
}

export function useDualStagePreapproveAllBulkAction() {
  const runBulkAction = useForegroundBulkAction();

  return (itemCount: number) =>
    runBulkAction({
      actionName: "Pre-approve payables",
      itemCount,
      confirmDescription: `Are you sure you want to pre-approve ${itemCount.toLocaleString()} payable${itemCount > 1 ? "s" : ""}?`,
      confirmLabel: "Yes, pre-approve",
      getItemCount: data => data.length,
      prepare: async () => {
        const fetched = await fetchPayables(
          API_QUERY_PAYABLES_DUAL_STAGE_TO_PREAPPROVE
        );
        return fetched.map<PayablesTableItemData>(payable => ({ payable }));
      },
      process: async (payables, onProgress) => {
        return processApprovePayables(
          payables,
          ClientWorkFlowStatus.PreApproved,
          onProgress
        );
      }
    });
}

export function useDualStageApproveAllBulkAction() {
  const runBulkAction = useForegroundBulkAction();

  return (itemCount: number) =>
    runBulkAction({
      actionName: "Approve payables",
      itemCount,
      confirmDescription: `Are you sure you want to approve ${itemCount.toLocaleString()} payable${itemCount > 1 ? "s" : ""}?`,
      confirmLabel: "Yes, approve",
      getItemCount: data => data.length,
      prepare: async () => {
        const fetched = await fetchPayables(
          API_QUERY_PAYABLES_DUAL_STAGE_TO_APPROVE
        );
        return fetched.map<PayablesTableItemData>(payable => ({ payable }));
      },
      process: async (payables, onProgress) => {
        return processApprovePayables(
          payables,
          ClientWorkFlowStatus.Approved,
          onProgress
        );
      }
    });
}
